import videojs from 'video.js';
import './captions-settings.css';
import { localStorageAvailable } from '../../util';

let localize;

class CaptionsSettingsPlugin extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        localize = player.localize?.bind(player);
        super(player, options);

        //when the player is ready, modify the DOM to match the new design for the caption settings
        player.ready(() => {
            //an object of partial id selectors paired with the value of what we want it to say
            //example: label id='captions-foreground-opacity-vjs_video_3_component_959' will then say localize("Text Opacity")
            const captionOptionLabels = {
                'captions-foreground-color': localize("Text Color"),
                'captions-foreground-opacity': localize("Text Opacity"),
                'captions-background-color': localize("Background Color"),
                'captions-background-opacity': localize("Background Opacity"),
                'captions-window-color': localize("Window Color"),
                'captions-window-opacity': localize("Window Opacity")
            };
            
            const captionOptionsParent = document.querySelector(".vjs-modal-dialog.vjs-text-track-settings > .vjs-modal-dialog-content"); 

            if (captionOptionsParent) {
                let captionHeader = document.createElement("h2");
                captionHeader.innerText = localize("Caption Settings");
                captionOptionsParent.prepend(captionHeader);
                const captionOptions = captionOptionsParent.querySelectorAll("fieldset.vjs-track-setting");

                for (let caption of captionOptions) {
                   this.#setupOptionDropdowns(caption);
                }

                //grab the label that corresponds with the option and set its text to be whatever the value is in captionOptionLabels
                for (const [captionLabelSelector, captionLabelWording] of Object.entries(captionOptionLabels)) {
                    let targetCaption = captionOptionsParent.querySelector(`label[id*=${captionLabelSelector}]`);
                    if (targetCaption) {
                        targetCaption.innerText = captionLabelWording;
                    }
                }

                const captionCloseBtn = document.querySelector(".vjs-modal-dialog.vjs-text-track-settings .vjs-done-button");
                captionCloseBtn.classList.add("player-primary-button");
                captionCloseBtn.innerText = localize("Update");

                //move the reset button to the bottom of its parent so that it is now the bottom button for screen readers
                captionCloseBtn.parentNode.prepend(captionCloseBtn);
            }

            //add custom fonts to the font dropdown
            this.addFont("Dyslexie", "dyslexie");
        });

        player.on("presentationloaded", () => {
            this.addHeaderToDropdown(localize("Captions"));
        });
    }

    //Adds a header to the captions selection dropdown, since one does not come by default
    addHeaderToDropdown(headerText) {
        const captionsDropdown = document.querySelector(".vjs-subs-caps-button > .vjs-menu .vjs-menu-content");
        const captionsHeader = document.createElement("li");
        captionsHeader.textContent = headerText;
        captionsHeader.classList.add("vjs-menu-title");
        captionsHeader.tabIndex = -1;

        captionsDropdown.prepend(captionsHeader);
    }

    /**
     * Displays a new font option based upon first parameter and adds a class based upon the second parameter (font-{fontClass}).
     * @param {string} fontDisplay
     * @param {string} fontClass
     */
    addFont(fontDisplay, fontClass) {
        const newFontOption = this.#createFontOption(fontDisplay, fontClass);

        //adds the new option to the caption font dropdown
        const fontMenuDropdown = document.querySelector(".vjs-font-family.vjs-track-setting select");
        fontMenuDropdown.add(newFontOption);   

        //adds a class to the 'vjs-text-track-display' element if the user previously selected that font
        const textTrackCue = document.querySelector('.vjs-text-track-display');
        let _localStorage = localStorageAvailable() ? localStorage : null;

        let currentCustomFont = _localStorage?.getItem('custom-text-track-settings');

        //sets the added font option as the active font option if selected previously
        if (currentCustomFont != null) {
            textTrackCue.classList.add(this.#generateFontClass(currentCustomFont));
           
            //set the active option to whatever was selected in our local storage            
            for (let i = 0; i < fontMenuDropdown.options.length; i++) {
                if (fontMenuDropdown.options[i].getAttribute('data-font') === currentCustomFont) {
                    fontMenuDropdown.selectedIndex = i;
                    break;
                }
            }
        }
        
        //whenever the font dropdown changes, we need to update the class added to the 'vjs-text-track-display' element
        fontMenuDropdown.addEventListener('change', (event) => {
            //remove the previously added font class, if applicable
            if (currentCustomFont != null) {
                textTrackCue.classList.remove(this.#generateFontClass(currentCustomFont));
            }

            //if the new option is a custom option, add a class and update local storage. If not, clear out the old value
            const updatedFont = event.target.selectedOptions[0].getAttribute('data-font');
            if (updatedFont != null) {
                textTrackCue.classList.add(this.#generateFontClass(updatedFont));
                _localStorage?.setItem('custom-text-track-settings', updatedFont);
            } else {
                _localStorage?.removeItem('custom-text-track-settings');
            }

            currentCustomFont = updatedFont;
        });
    }

    /**
     * Creates a new option element that's formatted with the arguments provided.
     * @param {any} fontDisplay
     * @param {any} fontClass
     */
    #createFontOption(fontDisplay, fontClass) {
        const newFontOption = document.createElement("option");
        newFontOption.textContent = localize(fontDisplay);
        newFontOption.setAttribute('value', 'default');
        newFontOption.setAttribute('data-font', fontClass);
        return newFontOption;
    }

    #generateFontClass(fontName) {
        return `font-${fontName}`;
    }

    /* 
     * The structure of some of the caption options are the following
     * <fieldset>
     *  <legend>Visible Title</legend>
     *  <label>Hidden Label</label>
     *  <select></select>
     *  <span>
     *      <label>Hidden Label</label>
     *      <select></select>
     *  </span>
     * </fieldset>
     * 
     * The goal is to hide the legend and instead make the labels be visual. To best make this work, we need to move the first select and label
     * to make them not direct children of the fieldset
     * 
     * <fieldset>
     *  <legend class="hidden">Hidden Title</legend>
     *  <span>
     *      <label>Visible Label</label>
     *      <select></select>
     *  </span>
     *  <span>
     *      <label>Visible Label</label>
     *      <select></select>
     *  </span>
     * </fieldset>
     */
    #setupOptionDropdowns(fieldsetEl) {
        //if there's more than one select, move element around. See method description
        if (fieldsetEl.querySelectorAll("select").length > 1) {

            //find and create the elements needed for the restructuring 
            let fieldsetLegend = fieldsetEl.querySelector("legend");
            let firstLabel = fieldsetEl.querySelector("legend + label");
            let firstSelect = fieldsetEl.querySelector("legend + label + select");
            let span = document.createElement("span");

            //move and hide the legend from being seen
            fieldsetLegend.classList.add("vjs-hidden");
            fieldsetLegend.after(span);

            //due to the query the reset function uses to find the select for each caption option
            //the select will need to have the same class list for its parent after moving
            span.classList = fieldsetEl.classList;

            //move the select and label to be children of the span we made            
            span.appendChild(firstLabel);
            span.appendChild(firstSelect);
        }

    }
}

export default CaptionsSettingsPlugin;